// src/context/LocationContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export const EXPLORE_RADIUS_MIN_KM = 5;
export const EXPLORE_RADIUS_MAX_KM = 100;
export const EXPLORE_RADIUS_DEFAULT_KM = 20;
const STORAGE_KEY = "m4nExploreRadiusKm";

interface Location {
  lat: number;
  lng: number;
  zoom?: number;
  /** Emphasize a highlight section on the map (rafting put-in/take-out). */
  sectionId?: string | null;
}

interface LocationContextProps {
  selectedLocation: Location | null;
  setSelectedLocation: React.Dispatch<React.SetStateAction<Location | null>>;
  /** Shared “around this place” search radius (km) — used on every place page + map ring. */
  exploreRadiusKm: number;
  setExploreRadiusKm: (km: number) => void;
}

const LocationContext = createContext<LocationContextProps | undefined>(
  undefined
);

interface LocationProviderProps {
  children: ReactNode;
}

function clampRadius(km: number): number {
  if (!Number.isFinite(km)) return EXPLORE_RADIUS_DEFAULT_KM;
  return Math.min(
    EXPLORE_RADIUS_MAX_KM,
    Math.max(EXPLORE_RADIUS_MIN_KM, Math.round(km))
  );
}

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  );
  const [exploreRadiusKm, setExploreRadiusKmState] = useState(
    EXPLORE_RADIUS_DEFAULT_KM
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw != null) setExploreRadiusKmState(clampRadius(Number(raw)));
    } catch {
      /* ignore */
    }
  }, []);

  const setExploreRadiusKm = useCallback((km: number) => {
    const next = clampRadius(km);
    setExploreRadiusKmState(next);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{
        selectedLocation,
        setSelectedLocation,
        exploreRadiusKm,
        setExploreRadiusKm,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextProps => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};
